<template>
  <div class="mt-12" v-if="modelValue?.phases?.length">
    <template v-for="(phase, index) in modelValue?.phases" :key="index">
      <AccordionItem
        v-if="phase.active"
        :title="getAccordionTitle(index + 1, country, wt)"
        :result="`${formatPhaseHours(getTotalHoursPerPhase(index, props.serviceCalculator), 10)} h`"
        description=""
        class="service-effort-accordion"
        :class="{
          'sub-accordion-wrapper': phasesConfiguration[index].showSubPhases,
          'border-t border-neutral-300': index === 0,
        }"
        :open="activePhaseAccordionKey === getPhaseAccordionKey(index)"
        :controlledOpen="true"
        @toggle="onPhaseAccordionToggle(index, $event)">
        <template
          v-if="
            phasesConfiguration[index].showPercentages && !phasesConfiguration[index].showSubPhases && phase.subPhases
          ">
          <SubPhasePercentagesInput
            v-model="phase.subPhases[0].percentages"
            :serviceHours="getServiceHours(index, 0, props.serviceCalculator)"
            :adjustedServiceHours="getAdjustedServiceHours(index, 0, props.serviceCalculator)"
            :serviceHoursSum="getServiceHoursSum(index, 0, props.serviceCalculator)"
            :form-error-prefix="`phases.${index}.subPhases.0`"
            :role="props.role"
            class="mt-3 sm:mt-6" />
        </template>
        <template v-if="phasesConfiguration[index].showAdditionalEfforts && phase.subPhases">
          <AdditionalEffortInput
            v-model="phase.subPhases[0]"
            :form-error-prefix="`phases.${index}.subPhases.0`"
            :role="props.role" />
        </template>

        <template v-if="phasesConfiguration[index].showSubPhases && phase.subPhases">
          <template v-for="(subPhase, subIndex) in phase.subPhases" :key="subIndex">
            <AccordionItem
              :title="getAccordionSubTitle(index + 1, subPhase.order, country, wt)"
              description=""
              class="sub-accordion"
              :class="{
                'first-sub-accordion': subIndex === 0,
                'last-sub-accordion': subIndex === phase.subPhases.length - 1,
              }"
              :open="activeSubAccordionKeys[getPhaseAccordionKey(index)] === getSubAccordionKey(index, subIndex)"
              :controlledOpen="true"
              @toggle="onSubAccordionToggle(index, subIndex, $event)">
              <SubPhasePercentagesInput
                v-model="subPhase.percentages"
                :serviceHours="getServiceHours(index, subIndex, props.serviceCalculator)"
                :adjustedServiceHours="getAdjustedServiceHours(index, subIndex, props.serviceCalculator)"
                :serviceHoursSum="getServiceHoursSum(index, subIndex, props.serviceCalculator)"
                :form-error-prefix="`phases.${index}.subPhases.${subIndex}`"
                :role="props.role"
                :key="`phase${index}${subIndex}-percentage`"
                class="mt-6" />
              <AdditionalEffortInput
                v-model="phase.subPhases[subIndex]"
                :form-error-prefix="`phases.${index}.subPhases.${subIndex}`"
                :role="props.role"
                :key="`phase${index}${subIndex}-effort`"
                class="mb-6" />
            </AccordionItem>
          </template>
        </template>
      </AccordionItem>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T extends { phases: ProjectPhase[] }">
import AccordionItem from '@/components/partials/layout/AccordionItem.vue'
import AdditionalEffortInput from '@/components/partials/inputs/AdditionalEffortInput.vue'
import SubPhasePercentagesInput from '@/components/partials/inputs/SubPhasePercentagesInput.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { computed, ref, toRef, type PropType } from 'vue'
import { useProjectGeneralCalculator } from '@/stores/projectCenter/projectGeneralCalculator'
import {
  formatPhaseHours,
  getAccordionSubTitle,
  getAccordionTitle,
  getAdjustedServiceHours,
  getPhasesConfiguration,
  getServiceHours,
  getServiceHoursSum,
  getTotalHoursPerPhase,
  type ServiceCalculator,
} from '../util'
import type { ProjectPhase } from '@/types'

export interface Props {
  role: string
  serviceCalculator: ServiceCalculator
}

const props = defineProps<Props>()
const { wt } = useWidgetText('widget-texts-project-service', toRef(props, 'role'))

const modelValue = defineModel({ type: Object as PropType<T> })

const projectGeneralCalculator = useProjectGeneralCalculator()

const country: string = (projectGeneralCalculator.projectGeneral?.country as string) || 'CH'

const phasesConfiguration = computed(() => {
  return getPhasesConfiguration(props.serviceCalculator, country)
})

const activePhaseAccordionKey = ref<string | null>(null)
const activeSubAccordionKeys = ref<Record<string, string | null>>({})

function getPhaseAccordionKey(index: number): string {
  return `${props.role}:phase:${index}`
}

function getSubAccordionKey(index: number, subIndex: number): string {
  return `${getPhaseAccordionKey(index)}:sub:${subIndex}`
}

function onPhaseAccordionToggle(index: number, open: boolean): void {
  const nextPhaseKey = open ? getPhaseAccordionKey(index) : null
  const previousPhaseKey = activePhaseAccordionKey.value

  activePhaseAccordionKey.value = nextPhaseKey

  if (!previousPhaseKey || previousPhaseKey === nextPhaseKey) return

  activeSubAccordionKeys.value = {
    ...activeSubAccordionKeys.value,
    [previousPhaseKey]: null,
  }
}

function onSubAccordionToggle(index: number, subIndex: number, open: boolean): void {
  const phaseKey = getPhaseAccordionKey(index)
  activeSubAccordionKeys.value = {
    ...activeSubAccordionKeys.value,
    [phaseKey]: open ? getSubAccordionKey(index, subIndex) : null,
  }
}
</script>

<style lang="postcss" scoped>
.service-effort-accordion {
  > :deep(.accordion-collapse > div) {
    @apply py-4;
  }
}
.sub-accordion {
  > :deep(.accordion-collapse > div) {
    @apply py-4;
  }
  &.first-sub-accordion {
    @apply border-t border-neutral-300;
  }
  &.last-sub-accordion {
    @apply border-b-0;
  }
  :deep(.core-accordion-header [role='heading']) {
    @apply text-base text-neutral-700;
  }
  :deep(.core-accordion-header svg) {
    @apply text-neutral-700;
  }
}
.sub-accordion-wrapper {
  > :deep(.accordion-collapse) {
    @apply mt-5 -mb-4;
  }

  > :deep(.accordion-collapse > div) {
    @apply py-0;
  }
}
</style>
